import { useRef, useState } from "react";

const MIN_WIDTH = 200;
const MAX_WIDTH = 600;
const CELL_PADDING = 24;

type UseColumnResizeReturn = {
  width: number;
  handleMouseDown: (event: React.MouseEvent) => void;
  handleDoubleClick: () => void;
  isResizing: boolean;
};

export function useColumnResize(
  initialWidth: number,
  containerSelector: string,
): UseColumnResizeReturn {
  const [width, setWidth] = useState(initialWidth);
  const [isResizing, setIsResizing] = useState(false);
  const startXRef = useRef(0);
  const startWidthRef = useRef(0);

  function handleMouseDown(event: React.MouseEvent) {
    event.preventDefault();
    startXRef.current = event.clientX;
    startWidthRef.current = width;
    setIsResizing(true);

    function handleMouseMove(moveEvent: MouseEvent) {
      const delta = moveEvent.clientX - startXRef.current;
      const newWidth = Math.min(
        MAX_WIDTH,
        Math.max(MIN_WIDTH, startWidthRef.current + delta),
      );
      setWidth(newWidth);
    }

    function handleMouseUp() {
      setIsResizing(false);
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    }

    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);
  }

  function handleDoubleClick() {
    const container = document.querySelector(containerSelector);

    if (!container) return;

    const cells = container.querySelectorAll<HTMLElement>(
      "td[data-data-room-column='tree']",
    );
    let maxContentWidth = MIN_WIDTH;

    const measured: {
      textSpan: HTMLElement;
      inner: HTMLElement;
      savedMaxWidth: string;
      savedWidth: string;
    }[] = [];

    // Write pass: remove constraints (no reflow yet)
    cells.forEach((cell) => {
      const inner = cell.querySelector<HTMLElement>("div");

      if (!inner) return;

      const textSpan = inner.querySelector<HTMLElement>("[title]");

      if (!textSpan) return;

      measured.push({
        textSpan,
        inner,
        savedMaxWidth: textSpan.style.maxWidth,
        savedWidth: inner.style.width,
      });
      textSpan.style.maxWidth = "none";
      inner.style.width = "max-content";
    });

    // Read pass: measure natural widths (single reflow)
    for (const entry of measured) {
      maxContentWidth = Math.max(
        maxContentWidth,
        entry.inner.offsetWidth + CELL_PADDING,
      );
    }

    // Write pass: restore constraints
    for (const entry of measured) {
      entry.textSpan.style.maxWidth = entry.savedMaxWidth;
      entry.inner.style.width = entry.savedWidth;
    }

    setWidth(Math.min(MAX_WIDTH, maxContentWidth));
  }

  return { width, handleMouseDown, handleDoubleClick, isResizing };
}
